import LoadingButton from '@mui/lab/LoadingButton';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import React, { useState } from 'react';
import { FolderTitleInput, intl as folderTitleInputIntl } from '../FolderTitleInput/FolderTitleInput';
import { useFolderTitle } from '../FolderTitleInput/hooks/useFolderTitle';
import { Folder } from '../../types';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { styles } from './CreateNewFolder.styles';

export const intl = {
  text: 'Create New Folder',
  cancel: 'Cancel',
  create: 'Create'
};

export const CreateNewFolder = ({
  parentFolderId,
  subfolders,
  onCreateFolder
}: {
  parentFolderId: Folder['id'];
  subfolders: Folder[];
  onCreateFolder: (folderId: Folder['id']) => void;
}) => {
  const [showInput, setShowInput] = useState(false);
  const { createFolder } = useFoldersContext();
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const { folderTitle, setFolderTitle, trimmedFolderTitle, isCreateDisabled, setError, error, helperTextWithIcon } =
    useFolderTitle({
      isCreatingFolder,
      folders: subfolders
    });

  const handleFolderTitleChange = (value: string) => {
    setError(null);
    setFolderTitle(value);
  };

  const handleCancel = () => {
    setShowInput(false);
    setFolderTitle(folderTitleInputIntl.defaultTitle);
  };

  const handleCreate = async () => {
    setError(null);
    setIsCreatingFolder(true);

    try {
      const createdFolderId = await createFolder({ title: trimmedFolderTitle, parentFolderId });
      if (createdFolderId) {
        onCreateFolder(createdFolderId);
        handleCancel();
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
    } finally {
      setIsCreatingFolder(false);
    }
  };

  return showInput ? (
    <Box sx={styles.container}>
      <FolderTitleInput
        disabled={isCreatingFolder}
        title={folderTitle}
        onChange={handleFolderTitleChange}
        error={error}
        helperText={helperTextWithIcon}
        onPressEnter={() => {
          if (!isCreateDisabled) {
            void handleCreate();
          }
        }}
      />
      <Button color="secondary" disabled={isCreatingFolder} onClick={handleCancel}>
        {intl.cancel}
      </Button>
      <LoadingButton
        variant="outlined"
        loading={isCreatingFolder}
        disabled={isCreateDisabled}
        onClick={() => {
          void handleCreate();
        }}
      >
        {intl.create}
      </LoadingButton>
    </Box>
  ) : (
    <Button
      startIcon={<AddIcon />}
      data-test="createNewFolderButton"
      onClick={() => {
        setShowInput(true);
      }}
    >
      {intl.text}
    </Button>
  );
};
